/**
 * Fixed-timestep helpers.
 *
 * VPGC sims run at a fixed integer-tick rate. The renderer accumulates real
 * elapsed time and asks "how many full ticks have I earned?". Float `dt` is
 * passed to `step()` purely for compatibility with the contract signature;
 * sim authors should use the integer tick counter as authority for any
 * time-dependent logic, never an accumulated float.
 */

export const TICK_HZ = 60;
export const TICK_DT_SEC = 1 / TICK_HZ;
export const TICK_DT_MS = 1000 / TICK_HZ;

/**
 * Convert a real-time delta into a number of full sim ticks plus carry.
 *
 * The carry is in milliseconds, not ticks; store it on the host (renderer
 * loop) and pass it back next frame. Never store carry inside GameState —
 * that would couple sim determinism to wall-clock pacing.
 */
export function ticksFromElapsed(
  elapsedMs: number,
  carryMs: number,
): { ticks: number; carryMs: number } {
  const total = elapsedMs + carryMs;
  if (total < TICK_DT_MS) return { ticks: 0, carryMs: total };
  const ticks = Math.floor(total / TICK_DT_MS);
  return { ticks, carryMs: total - ticks * TICK_DT_MS };
}

/** Convert a duration in ms to whole ticks (truncating). */
export function msToTicks(ms: number): number {
  return Math.floor(ms / TICK_DT_MS);
}

/** Convert ticks to ms. */
export function ticksToMs(ticks: number): number {
  return ticks * TICK_DT_MS;
}
